import {
  collection,
  getDocs,
  getDoc,
  doc,
  updateDoc,
  query,
  where,
} from 'firebase/firestore';
import { db } from '../../firebase/firebase';
import { diffGroupIds, hasAccessViaGroups } from './projectNormalisers';
import { addUserToProject, removeUserFromProject } from './access';

export async function getUserGroups(userId: string): Promise<string[]> {
  try {
    const groupsRef = collection(db, 'groups');
    const groupsQuery = query(groupsRef, where('memberIds', 'array-contains', userId));
    const groupsSnapshot = await getDocs(groupsQuery);
    return groupsSnapshot.docs.map((groupDoc) => groupDoc.id);
  } catch (error) {
    console.error('Error getting user groups:', error);
    return [];
  }
}

/**
 * Updates project access for users when group membership changes.
 */
export async function syncGroupMembershipChanges(
  groupId: string,
  addedUserIds: string[],
  removedUserIds: string[]
): Promise<void> {
  try {
    const projectsRef = collection(db, 'Projects');
    const projectsQuery = query(projectsRef, where('groupIds', 'array-contains', groupId));
    const projectsSnapshot = await getDocs(projectsQuery);

    const updatePromises = projectsSnapshot.docs.flatMap(async (projectDoc) => {
      const project_id = projectDoc.id;
      const projectData = projectDoc.data();

      if (!projectData.isPrivate) {
        return [];
      }

      const addPromises = addedUserIds.map(async (userId) => {
        try {
          await addUserToProject(userId, project_id, 'read');
          return { userId, success: true };
        } catch (error) {
          console.error(`[ProjectManager] Failed to add access for user ${userId}:`, error);
          return { userId, success: false, error };
        }
      });

      const removePromises = removedUserIds.map(async (userId) => {
        const userGroups = await getUserGroups(userId);
        const projectGroups = projectData.groupIds || [];
        const hasOtherAccess = hasAccessViaGroups(userGroups, projectGroups);

        if (!hasOtherAccess) {
          await removeUserFromProject(userId, project_id);
        }
      });

      return [...addPromises, ...removePromises];
    });

    await Promise.all(updatePromises.flat());
  } catch (error) {
    console.error(`[ProjectManager] Error syncing group membership changes:`, error);
    throw error;
  }
}

export async function syncProjectGroupChanges(
  project_id: string,
  oldGroupIds: string[],
  newGroupIds: string[]
): Promise<void> {
  try {
    const projectRef = doc(db, 'Projects', project_id);
    const projectDoc = await getDoc(projectRef);
    if (!projectDoc.exists()) {
      console.error(`Project ${project_id} does not exist`);
      return;
    }
    const projectData = projectDoc.data();
    if (!projectData.isPrivate) {
      return;
    }

    const { addedGroupIds, removedGroupIds } = diffGroupIds(oldGroupIds, newGroupIds);

    for (const groupId of addedGroupIds) {
      const groupRef = doc(db, 'groups', groupId);
      const groupDoc = await getDoc(groupRef);
      if (groupDoc.exists()) {
        const memberIds = groupDoc.data().memberIds || [];

        const projectAccessRef = collection(db, 'ProjectAccess');
        const accessQuery = query(
          projectAccessRef,
          where('project_id', '==', project_id),
          where('userId', 'in', memberIds)
        );
        const existingAccess = await getDocs(accessQuery);
        const existingAccessMap = new Map(
          existingAccess.docs.map((accessDoc) => [accessDoc.data().userId, accessDoc.data()])
        );

        await Promise.all(
          memberIds.map(async (userId: string) => {
            try {
              if (!existingAccessMap.has(userId)) {
                await addUserToProject(userId, project_id, 'read');
                return { userId, success: true, action: 'added' };
              }
              return { userId, success: true, action: 'already_exists' };
            } catch (error) {
              console.error(`Failed to add access for user ${userId}:`, error);
              return { userId, success: false, error };
            }
          })
        );
      }
    }

    for (const groupId of removedGroupIds) {
      const groupRef = doc(db, 'groups', groupId);
      const groupDoc = await getDoc(groupRef);
      if (groupDoc.exists()) {
        const memberIds = groupDoc.data().memberIds || [];
        const projectAccessRef = collection(db, 'ProjectAccess');
        const accessQuery = query(
          projectAccessRef,
          where('project_id', '==', project_id),
          where('userId', 'in', memberIds)
        );
        const existingAccess = await getDocs(accessQuery);
        const existingAccessMap = new Map(
          existingAccess.docs.map((accessDoc) => [
            accessDoc.data().userId,
            { ...accessDoc.data(), docId: accessDoc.id },
          ])
        );

        await Promise.all(
          memberIds.map(async (userId: string) => {
            try {
              const userGroups = await getUserGroups(userId);
              const hasOtherAccess = hasAccessViaGroups(userGroups, newGroupIds);

              if (!hasOtherAccess) {
                if (existingAccessMap.has(userId)) {
                  await removeUserFromProject(userId, project_id);
                  return { userId, removed: true, hadAccess: true };
                }
                return { userId, removed: false, hadAccess: false, reason: 'no_access_record' };
              }
              return {
                userId,
                removed: false,
                hadAccess: existingAccessMap.has(userId),
                reason: 'has_other_access',
              };
            } catch (error) {
              console.error(`Failed to process removal for user ${userId}:`, error);
              return { userId, removed: false, error };
            }
          })
        );
      }
    }

    await updateDoc(projectRef, {
      groupIds: newGroupIds,
    });
  } catch (error) {
    console.error('Error in syncProjectGroupChanges:', error);
    throw error;
  }
}
